<template>
  <div>
    <ag-grid-vue
      :class="['table ag-grid-table', { hasClickableRows }]"
      :rowData="rows"
      :pagination="false"
      :customRow="customRow"
      :defaultColDef="defaultColDef"
      :rowHeight="53"
      :headerHeight="headerHeight"
      :suppressDragLeaveHidesColumns="!canHideColumns"
      :suppressHorizontalScroll="false"
      :unSortIcon="true"
      :icons="icons"
      :tooltipShowDelay="0"
      :tooltipHideDelay="2000"
      :enableBrowserTooltips="true"
      @grid-ready="onGridReady"
      @sort-changed="sortChanged"
      @column-visible="hideColumn($event)"
      @column-moved="onColDrag"
      @drag-stopped="onColDragEnd"
      @column-resized="resized($event)"
      @rowClicked="cellClick($event.node.data, $event)"
    >
      <ag-grid-column
        v-for="(column, idx) in columns"
        :key="idx"
        :field="column.index"
        :headerName="column.label"
        :sortable="column.sortable"
        :sort="column.defaultSortOrder"
        :colId="column.index"
        :initialWidth="column.width || 200"
      />
    </ag-grid-vue>

    <a-popover
      v-model="editingCell.show"
      trigger="click"
      placement="bottomLeft"
      overlayClassName="table-edit-popover"
      :align="{
        target: editingCell.container,
        offset: editPopupOffset,
      }"
    >
      <template #content>
        <table-cell-editable
          v-if="editingCell.show"
          v-model="editingCell.value"
          :column="editingCell.column"
          :entity="editingCell.entity"
          @stopEdit="stopEditCell"
        />
      </template>
    </a-popover>
  </div>
</template>

<script>
import {AgGridVue} from 'ag-grid-vue';
import store from '@/store';
import CONFIG_QUERY from '@/queries/config';
import TableCell from './TableCell.vue';
import TableCellEditable from './TableCellEditable.vue';

const SORT_DIRECTION_API_MAPPING = {
  ascend: 'ASC',
  descend: 'DESC',
  asc: 'ASC',
  desc: 'DESC',
};

// function headerHeightGetter() {
//   var columnHeaderTexts = [
//     ...document.querySelectorAll('.ag-header-cell-text'),
//   ];
//   var clientHeights = columnHeaderTexts.map(
//     headerText => headerText.clientHeight
//   );
//   var tallestHeaderTextHeight = Math.max(...clientHeights);
//
//   return tallestHeaderTextHeight;
// }


export default {
  name: 'TheTable',

  components: {
    // eslint-disable-next-line vue/no-unused-components
    TableCell,
    TableCellEditable,
    AgGridVue,
  },

  props: {
    config: {
      type: Object,
      required: true,
    },
    formConfig: {
      type: Object,
      default: null,
    },
    defaultSort: {
      type: Array,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    entityFields: {
      type: Array,
      default: () => [],
    },
    canReadModel: {
      type: Boolean,
      default: false,
    },
    canUpdateModel: {
      type: Boolean,
      default: false,
    },
    canHideColumns: {
      type: Boolean,
      default: true,
    },
    elasticTable: {
      type: Boolean,
      default: false,
    },
    headerHeight: {
      type: Number,
      default: 53,
    },
  },

  apollo: {
    sortableFields: {
      ...CONFIG_QUERY,
      variables() {
        return {
          name: 'glob_SortableFields',
        };
      },
      update({table}) {
        if (table?.totalCount) {
          return JSON.parse(table.documents[0].data.config);
        }
        return [];
      },
      error(error, vm, key, type, {variables}) {
        this.emitError(
          `Ошибка получения сортируемых полей. Name: ${variables.name}. ${error.message}`,
        );
      },
    },
  },

  data() {
    return {
      editingCell: {
        show: false,
        entity: null,
        column: null,
        value: null,
        container: null,
      },
      gridApi: null,
      newIndex: null,
      fieldId: null,
      startConfIndex: null,
      columnDefs: [],
      icons: {
        sortAscending: '<i class="fa fa-sort-alpha-up"/>',
        sortDescending: '<i class="fa fa-sort-alpha-down"/>',
      },
      defaultColDef: {
        resizable: true,
        cellRendererFramework: 'TableCell',
        // wrapText: true,     // <-- HERE
        // autoHeight: true,   // <-- & HERE
        // headerComponentParams: {
        //   template:
        //     '<div class="ag-cell-label-container" role="presentation">' +
        //     '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
        //     '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
        //     '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
        //     '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
        //     '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
        //     '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
        //     '    <span ref="eText" class="ag-header-cell-text" role="columnheader" style="white-space: normal;"></span>' +
        //     '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
        //     '  </div>' +
        //     '</div>',
        // },
        cellRendererParams: {
          canUpdateModel: this.canUpdateModel,
          cell: this,
          click: this.cellClick,
          checkboxChanged: this.checkboxChanged,
          clickAction: this.clickAction,
          goToPhrases: this.goToPhrases,
          cancelAdjustment: this.cancelAdjustment,
          changeBooleanQuality: this.changeBooleanQuality,
        },
        cellStyle: params => {
          if (params.value?.imputationCode) {
            //mark police cells as red
            const code = params.value.imputationCode
            if (code === 2){
              return {backgroundColor: '#ffdcdc'}
            }
            if (code === 1){
              return {backgroundColor: '#fff6d4'}
            }
          }
          return null;
        }
      },
    };
  },

  computed: {
    desktop() {
      return store.state.isDesktop;
    },
    columns() {
      return this.prepareColumnsForAnt(this.entityFields);
    },
    rows() {
      return this.prepareRowsDataForAnt(this.data.rows);
    },
    hasClickableRows() {
      return this.canReadModel || this.canUpdateModel;
    },
    editable() {
      return this.config.displayType?.options?.editable;
    },
    editPopupOffset() {
      const renderer = this.editingCell.column?.renderer;
      let offset = [-28, -46];
      if (renderer === 'color') offset = [-16, -46];
      else if (['ref', 'refs'].includes(renderer)) offset = [-10, -49];

      return offset;
    },
  },

  watch: {
    columns(newVal) {
      const gridColumns = this.gridApi?.getColumnDefs();
      if (newVal && gridColumns) {
        const newColumns = [];
        newVal.forEach((col) => {
          const column = {};
          Object.entries(gridColumns[0]).forEach(([key, value]) => {
            if (key !== 'pivot' && key !== 'rowGroup' && key !== 'minWidth' && key !== 'flex') {
              column[key] = value;
            }
          });
          column.field = col.index;
          column.headerName = col.label;
          column.sortable = col.sortable;
          column.sortingOrder = ['asc', 'desc', null];
          column.sort = col.defaultSortOrder;
          column.colId = col.index;
          column.width = col.width || 200;
          column.hide = col.hidden;
          column.tooltipField = col.index
          column.unSortIcon = true;
          column.comparator = () => {
          };
          if (col.renderer === 'ann_mounth') {
            column.cellClass = function (params) {
              const data = params.data[params.colDef.field] || null;
              const imputation_code = data?.imputationCode;
              return imputation_code ? `impCode_${imputation_code}` : '';
            };
          }
          newColumns.push(column);
        });
        this.gridApi.setColumnDefs(newColumns);
        if (
          this.gridApi.columnModel?.bodyWidth <
          100 + document.getElementsByClassName('ag-grid-table')[0].clientWidth
        ) {
          newColumns[newColumns.length - 1].flex = 1;
          newColumns[newColumns.length - 1].minWidth = 200;
          this.dontSaveChanges = true
        } else {
          newColumns[newColumns.length - 1].flex = null;
          newColumns[newColumns.length - 1].minWidth = null;
        }
        this.gridApi.setColumnDefs(newColumns);
      }
    },
  },

  created() {
    this.icons = {
      sortUnSort:
      // '<a-icon type="caret-up" />',
        '<span class="ant-table-column-sorter sort-unsort">' +
        '<div title="Sort" class="ant-table-column-sorter-inner ant-table-column-sorter-inner-full">' +
        '<i aria-label="icon: caret-up" class="anticon anticon-caret-up ant-table-column-sorter-up off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-up" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z"></path></svg>' +
        '</i>' +
        '<i aria-label="icon: caret-down"class="anticon anticon-caret-down ant-table-column-sorter-down off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-down" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"></path></svg>' +
        '</i>' +
        '</div>' +
        '</span>',
      sortAscending:
        '<span class="ant-table-column-sorter sort-asc">' +
        '<div title="Sort" class="ant-table-column-sorter-inner ant-table-column-sorter-inner-full">' +
        '<i aria-label="icon: caret-up" class="anticon anticon-caret-up ant-table-column-sorter-up off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-up" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z"></path></svg>' +
        '</i>' +
        '<i aria-label="icon: caret-down" class="anticon anticon-caret-down ant-table-column-sorter-down off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-down" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"></path></svg>' +
        '</i>' +
        '</div>' +
        '</span>',
      sortDescending:
        '<span class="ant-table-column-sorter sort-desc">' +
        '<div title="Sort" class="ant-table-column-sorter-inner ant-table-column-sorter-inner-full">' +
        '<i aria-label="icon: caret-up" class="anticon anticon-caret-up ant-table-column-sorter-up off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-up" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M858.9 689L530.5 308.2c-9.4-10.9-27.5-10.9-37 0L165.1 689c-12.2 14.2-1.2 35 18.5 35h656.8c19.7 0 30.7-20.8 18.5-35z"></path></svg>' +
        '</i>' +
        '<i aria-label="icon: caret-down" class="anticon anticon-caret-down ant-table-column-sorter-down off">' +
        '<svg viewBox="0 0 1024 1024" data-icon="caret-down" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"></path></svg>' +
        '</i>' +
        '</div>' +
        '</span>',
    };
  },

  methods: {
    headerHeightSetter() {
      var padding = 20;
      var height = headerHeightGetter() + padding;
      this.gridApi.setHeaderHeight(height);
      this.gridApi.resetRowHeights();
    },
    onGridReady(params) {
      this.gridApi = params.api;
      const newColumns = this.gridApi.getColumnDefs()
      if (
        this.gridApi.columnModel?.bodyWidth <
        100 + document.getElementsByClassName('ag-grid-table')[0].clientWidth
      ) {
        newColumns[newColumns.length - 1].flex = 1;
        newColumns[newColumns.length - 1].minWidth = 200;
        this.dontSaveChanges = true
      }
      this.gridApi.setColumnDefs(newColumns);

    },
    customRow(data) {
      return {
        on: {
          click: () => {
            if (!this.editable) this.$emit('entityClick', data.id, data.type, data.data);
          },
        },
      };
    },
    async clickAction(entity) {
      this.$emit('clickAction', entity);
    },
    async goToPhrases(entity) {
      this.$emit('goToPhrases', entity);
    },
    async cancelAdjustment(entity) {
      this.$emit('cancelAdjustment', entity);
    },
    async changeBooleanQuality(row, col, newVal) {
      this.$emit('changeBooleanQuality', row, col, newVal);
    },
    stopEditCell() {
      const entity = this.rows.find((row) => row.id === this.editingCell.entity.id);
      entity.data[this.editingCell.column.name] = this.editingCell.value;
      this.$emit('updateEntity', entity.id, entity.data, {
        [this.editingCell.column]: this.editingCell.value,
      });
      this.editingCell = {
        show: false,
        entity: null,
        column: null,
        value: null,
        container: null,
      };
    },
    resized(event) {
      // this.headerHeightSetter()
      if (event.finished && event.column && event.column.colId !== 'data.' && !this.dontSaveChanges) {
        let col = event.columns[0].colId;
        if (col.split('.').length > 1) {
          col = col.substr(col.indexOf('.') + 1);
        }
        const elId = this.config.displayFields.find((el) => el.field === col);
        if (elId.width !== event.columns[0].actualWidth) {
          elId.width = event.columns[0].actualWidth;
          this.$emit('configUpdated');
        }
      }
      this.dontSaveChanges = false
    },
    onColDragEnd() {
      if (this.newIndex !== null && this.fieldId !== null && this.startConfIndex !== null) {
        const conf = this.config.displayFields;
        if (this.newIndex === 0) {
          conf.splice(this.newIndex, 0, conf.splice(this.startConfIndex, 1)[0]);
          this.$emit('configUpdated');
        } else {
          let prevEl = this.gridApi?.getColumnDefs()[this.newIndex - 1].field;
          if (prevEl.split('.').length > 1) {
            prevEl = prevEl.substr(prevEl.indexOf('.') + 1);
          }
          let prevElConfIndex = this.config.displayFields.findIndex((el) => el.field === prevEl);
          if (prevElConfIndex + 1 <= this.startConfIndex) {
            prevElConfIndex += 1;
          }
          conf.splice(prevElConfIndex, 0, conf.splice(this.startConfIndex, 1)[0]);
          this.$emit('configUpdated');
        }
      }
      this.newIndex = null;
      this.fieldId = null;
      this.startConfIndex = null;
    },
    onColDrag(event) {
      if (event.column) {
        this.newIndex = event.toIndex;
        this.fieldId = event.column.colId;
        if (this.fieldId.split('.').length > 1) {
          this.fieldId = this.fieldId.substr(this.fieldId.indexOf('.') + 1);
        }
        this.startConfIndex = this.config.displayFields.findIndex((el) => el.field === this.fieldId);
      }
    },
    hideColumn(event) {
      let colField = event.columns[0].colId;
      if (colField.split('.').length > 1) {
        colField = colField.substr(colField.indexOf('.') + 1);
      }
      const column = this.config.displayFields.find((col) => col.field === colField);
      column.hidden = 'true';
      this.$emit('configUpdated');
    },
    sortChanged({api}) {
      const column = api.getColumnDefs().find((col) => col.sort !== null);
      let sortParams = [];

      if (column) {
        const direction = SORT_DIRECTION_API_MAPPING[column.sort];

        if (direction) {
          const field = column.colId.replace(/.*?\.(.*)/, '$1');
          sortParams = [{direction, field}];
        }
      }

      this.$emit('setSortParams', sortParams);
    },
    cellClick(data, event) {
      if (!this.editable) {
        if (this.elasticTable) this.$emit('entityClick', data);
        else this.$emit('entityClick', data.id, data.type, data.data);
      }
    },
    isColumnSortable({sortable, renderer}) {
      return sortable !== undefined ? sortable : this.sortableFields?.includes(renderer);
    },
    getColumnSortOrder(column) {
      const sort = this.defaultSort.find((sortCol) => sortCol.field === column.field)?.direction;
      return sort ? {ASC: 'asc', DESC: 'desc'}[sort] : null;
    },
    prepareColumnsForAnt(entityFields) {
      const columns = this.config.displayFields
        .filter((column) => !column.hidden)
        .map((column) => {
          column = {
            ...column,
            ...(entityFields.find((field) => column.field === field.name) || {}),
            label:
              this.formConfig?.form.locale[store.state.lang].fields[column.field] || column.label,
          };
          column.index = column.index || `data.${column.field}`;
          column.sortable = this.isColumnSortable(column);
          column.defaultSortOrder = this.getColumnSortOrder(column);
          column.hidden = false;
          return column;
        });

      if (this.editable)
        columns.push({
          name: 'actions',
          label: '',
        });
      return columns;
    },
    prepareRowsDataForAnt(rows) {
      return rows.length ? rows.map((row, key) => {
        if (row && typeof row === 'object' && 'status' in row) {
          if (row.status === 'Обновлен') {
            row.status = `🟢 ${row.status}`;
          } else {
            row.status = `🟡 ${row.status}`;
          }
        }
        return {...row, key};
      }) : Array.from(Array(10).keys());
    },
    checkboxChanged({target: {checked}}, entity, field) {
      entity.data[field] = checked;
      this.$emit('updateEntity', entity.id, entity.data, {[field]: checked});
    },
  },
};
</script>

<style lang="scss">

.stat-page__table {
  @include scrollbars();
  overflow-x: auto;
}

.table {
  @include scrollbars();
  color: $darkTextColor;
  font-size: 13px;

  .ag-root ::-webkit-scrollbar {
    width: .2em;
    height: 1em;
  }

  .ag-root ::-webkit-scrollbar-track-piece {
    background: none;
  }

  .ag-root ::-webkit-scrollbar-thumb {
    background: $baseColor;
  }

  .tags__item.ant-tag {
    margin-top: 0;
  }

  .tags {
    margin-top: -5px;
  }

  .ag-root-wrapper-body.ag-layout-normal {
    height: auto;
  }

  .ag-header-cell {
    font-weight: 700;
  }

  .ag-cell,
  .ag-header-cell {
    padding: 16px;
    overflow-wrap: break-word;
  }

  td,
  .ag-cell {
    white-space: nowrap;
  }

  &.hasClickableRows {
    tbody tr,
    .ag-row {
      cursor: pointer;
    }
  }

  .ant-table-column-title,
  .ag-header-cell-text {
    white-space: nowrap;
    padding-right: 5px;
  }

  .ant-table-column-sorter-inner {
    display: flex;
    flex-direction: column;
    color: #bfbfbf;

    .anticon {
      color: inherit;
    }
  }

  .sort-asc {
    .anticon-caret-up {
      color: #366dbb;
    }
  }

  .sort-desc {
    .anticon-caret-down {
      color: #366dbb;
    }
  }

  &__text {
    min-height: 1.6em;

    &--short {
      max-width: 50px;
    }
  }

  &__color-field {
    width: 122px;
  }

  &__actions {
    visibility: visible;
    text-align: center;
  }

  &__gallery {
    margin: -20px 0px;
    max-width: 300px;

    img {
      & + img {
        margin-left: 5px;
      }
    }
  }

  .ant-table-placeholder {
    display: none;
  }

  &.ant-table-wrapper,
  &.ag-grid-table {
    @include scrollbars();
    overflow-x: auto;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: $borderRadius;

    .ag-overlay {
      position: initial;

      .ag-overlay-no-rows-wrapper {
        min-height: 50px;
      }
    }
  }

  // For editable cells
  .ant-form-item {
    margin: 0;
  }

  .ant-form-item-control {
    line-height: 24px;
  }
}

.ag-dnd-ghost {
  border: solid 1px;
  border-color: $defaultBorderColor;
  background: $blockGrayBg;
  border-radius: 3px;
  box-shadow: 0 1px 4px 1px rgba(186, 191, 199, 0.4);
  overflow: hidden;
  text-overflow: ellipsis;
  color: $darkTextColor;
  height: 48px !important;
  line-height: 48px;
  margin: 0;
  padding: 0 12px;
  -webkit-transform: translateY(12px);
  transform: translateY(12px);
}

.ag-dnd-ghost-icon {
  color: $darkTextColor;
}

.table-edit-popover {
  .ant-popover-arrow {
    display: none;
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-control {
    line-height: 32px;
  }

  .ant-popover-inner-content {
    padding-right: 2px;
  }
}

.annual_catalog {
  .table .ag-cell {
    &.impCode_2 {
      background: #ffdcdc;
    }

    &.impCode_1 {
      background: #fff6d4;
    }
  }

  .ag-row-hover .ag-cell {
    &.impCode_2 {
      background: darken(#ffdcdc, 10%);
    }

    &.impCode_1 {
      background: darken(#fff6d4, 10%);
    }
  }
}
</style>
